import { LICENCES } from '../../generated/licences'
import { Langtext } from '../../components/Langtext'
import { useSettings } from '../../hooks/queries'

/**
 * Which open-source projects this application uses — visible to everyone.
 *
 * From the log: „haben wir Code aus anderen open source Projekten verwendet?
 * wenn ja, müssten wir das anzeigen - oder? Eigene Kategorie in Einstellungen,
 * für alle sichtbar." And, rightly, a follow-up: „und was ist mit MariaDB?"
 *
 * The question has three answers, and they differ:
 *
 *  - **In the browser** seven packages ship inside the bundle. Naming them is
 *    not politeness: MIT and BSD require the copyright notice and the licence
 *    text to travel with the software, and this software travels on every page
 *    load. The list is generated (`tools/collect-licences.mjs`), never typed —
 *    licence notices carry names and years that nobody may guess.
 *  - **In our own server code, not one foreign line.** `composer.json` asks for
 *    PHP, PDO and mbstring; PHPUnit is a development tool. That is not thrift,
 *    it is the target: shared hosting has no Composer.
 *  - **Underneath, plenty** — and that is the follow-up question. PHP and
 *    MariaDB carry the whole thing. We do not ship them, so no obligation
 *    follows; a list without them would still be dishonest.
 *
 * The interface stays German — the people reading it are. Its sentences were
 * rewritten in 0.48.1: they had inherited the tone of these comments
 * (dashes, reasons, "Tragen tun ihn"), and from use came "ein sehr komisches
 * Deutsch". The rule is in CLAUDE.md: a sentence in the interface says what
 * something does, nothing more.
 */
export function OpenSourceCard() {
  const settings = useSettings()
  // The versions come for admins only (1.7.0): a reader sees the two
  // projects and their licences, not which build runs.
  const laufzeit = settings.data?.runtime ?? null
  const dienste = settings.data?.services ?? null

  return (
    <>
      <section className="card card--seite">
        <h2 className="card__titel">Diese Anwendung</h2>
        {/* AGPL since 0.30.7: whoever uses the application over the network
            is entitled to its source. The link is that offer, on a page every
            signed-in person can reach -- not a courtesy but section 13. */}
        <p className="feld__hinweis feld__hinweis--13">
          Travel Broadcast steht unter der Lizenz <strong>AGPL-3.0-or-later</strong>.
          Wer die Anwendung über das Netz nutzt, hat ein Recht auf ihren
          Quelltext. Den Quelltext und den vollständigen Lizenztext findest du im
          Repository:{' '}
          <a
            href="https://github.com/martinB65/Travel-Broadcast"
            target="_blank"
            rel="noreferrer noopener"
          >
            github.com/martinB65/Travel-Broadcast
          </a>
          .
        </p>
      </section>

      <section className="card card--seite">
        <h2 className="card__titel">Im Buch</h2>
        <p className="feld__hinweis feld__hinweis--13">
          Diese drei Werke stecken in jedem PDF, das der Server erzeugt. Ihre
          Lizenztexte liegen in der Releaseversion im Ordner <code>fonts/</code>.
        </p>
        <ul className="lizenzen">
          <li className="lizenz">
            <p className="lizenz__kopf">
              <strong>DejaVu Sans</strong> · <span className="lizenz__marke">Bitstream Vera License</span>
            </p>
            <p className="feld__hinweis">
              Die Schrift im PDF. Copyright (c) 2003 by Bitstream, Inc.; DejaVu
              changes are in public domain.{' '}
              <a href="https://dejavu-fonts.github.io/" target="_blank" rel="noreferrer noopener">
                dejavu-fonts.github.io
              </a>
            </p>
          </li>
          <li className="lizenz">
            <p className="lizenz__kopf">
              <strong>Noto Emoji</strong> · <span className="lizenz__marke">SIL Open Font License 1.1</span>
            </p>
            <p className="feld__hinweis">
              Die Emojis im PDF, als Bilder. Copyright 2013 Google LLC.{' '}
              <a href="https://github.com/googlefonts/noto-emoji" target="_blank" rel="noreferrer noopener">
                github.com/googlefonts/noto-emoji
              </a>
            </p>
          </li>
          <li className="lizenz">
            <p className="lizenz__kopf">
              <strong>region-flags</strong> · <span className="lizenz__marke">Public Domain</span>
            </p>
            <p className="feld__hinweis">
              Die Flaggen unter den Emojis, aus demselben Repository.
            </p>
          </li>
        </ul>
      </section>

      <section className="card card--seite">
        {/* Merkmal für den Prüflauf (1.23.0): der Wortlaut dieser Karte steht
            als Lizenznennung nicht in `de.ts`, also gibt es keinen Schlüssel,
            an dem ein Lauf sich halten könnte. */}
        <h2 className="card__titel" data-abschnitt="browser">Im Browser</h2>
        <p className="feld__hinweis feld__hinweis--13">
          Diese {LICENCES.length} Projekte sind Teil der Oberfläche, die dein
          Gerät lädt. Ihre Lizenzen verlangen, dass Hinweis und Lizenztext
          mitgeliefert werden. Deshalb stehen sie hier vollständig.
        </p>
        <ul className="lizenzen">
          {LICENCES.map((eintrag) => (
            <li key={eintrag.name} className="lizenz">
              <p className="lizenz__kopf">
                <strong>{eintrag.name}</strong> {eintrag.version} ·{' '}
                <span className="lizenz__marke">{eintrag.licence}</span>
              </p>
              {eintrag.homepage !== '' && (
                <p className="feld__hinweis">
                  <a href={eintrag.homepage} target="_blank" rel="noreferrer noopener">
                    {eintrag.homepage}
                  </a>
                </p>
              )}
              {/* The full text, folded away — the same component the long trip
                  reports use. Seven notices unfolded would bury the list. */}
              <Langtext text={eintrag.text} zeilen={4} ab={200}>
                <pre className="lizenz__text">{eintrag.text}</pre>
              </Langtext>
            </li>
          ))}
        </ul>
      </section>

      <section className="card card--seite">
        <h2 className="card__titel">Auf dem Server</h2>
        <p className="feld__hinweis feld__hinweis--13">
          Der Servercode verwendet keine fremden Bibliotheken. Auf geteiltem
          Webhosting gibt es keinen Composer, deshalb kommt er ohne aus.
        </p>
        <p className="feld__hinweis feld__hinweis--13">
          Zwei fremde Projekte laufen darunter. Sie gehören{' '}
          <strong>nicht zur Anwendung</strong>, der Server stellt sie bereit:
        </p>
        <ul className="lizenzen">
          <li className="lizenz">
            <p className="lizenz__kopf">
              <strong>PHP</strong>{laufzeit ? ` ${laufzeit.php}` : ''} ·{' '}
              <span className="lizenz__marke">PHP License</span>
            </p>
            <p className="feld__hinweis">
              <a href="https://www.php.net/license/" target="_blank" rel="noreferrer noopener">
                php.net/license
              </a>
            </p>
          </li>
          <li className="lizenz">
            {/* **Das Merkmal sagt, ob die Fassungen schon da sind** (1.23.0).
                Sie kommen über `/api/settings`; ein Prüflauf hing davor an
                einem Wortlaut, der so nie dastand, und wartete damit gar
                nicht. */}
            <p className="lizenz__kopf" data-laufzeit={laufzeit ? 'da' : 'fehlt'}>
              <strong>MariaDB</strong>{laufzeit ? ` ${laufzeit.database}` : ''} ·{' '}
              <span className="lizenz__marke">GPL-2.0</span>
            </p>
            <p className="feld__hinweis">
              <a href="https://mariadb.com/kb/en/mariadb-license/" target="_blank" rel="noreferrer noopener">
                mariadb.com/kb/en/mariadb-license
              </a>
            </p>
          </li>
        </ul>
        <p className="feld__hinweis">
          Die Versionsnummern zeigen, was der Server gerade meldet.
        </p>
      </section>

      <section className="card card--seite">
        <h2 className="card__titel">Dienste</h2>
        <p className="feld__hinweis feld__hinweis--13">
          Diese Dienste liefern keinen Code, sind aber in der Anwendung zu sehen.
        </p>
        <ul className="lizenzen">
          <li className="lizenz">
            <p className="lizenz__kopf">
              <strong>Kartenkacheln</strong>
            </p>
            {/* The notice comes from `MapSources` by way of the settings, never
                typed here: whoever switches the source switches it with them. */}
            <p
              className="feld__hinweis"
              dangerouslySetInnerHTML={{ __html: settings.data?.map?.attribution ?? '' }}
            />
          </li>
          {dienste?.gif === true && (
            <li className="lizenz">
              <p className="lizenz__kopf">
                <strong>GIPHY</strong>
              </p>
              <p className="feld__hinweis">
                Die GIFs in den Antworten kommen von GIPHY.
              </p>
            </li>
          )}
          {(dienste?.place === true || dienste?.weather === true) && (
            <li className="lizenz">
              <p className="lizenz__kopf">
                <strong>Apple Maps &amp; Weather</strong>
              </p>
              <p className="feld__hinweis">
                Ortsnamen und Wetter zu einem Punkt holt der Server bei Apple.
                Die Wetterdaten stammen von{' '}
                <a
                  href="https://developer.apple.com/weatherkit/data-source-attribution/"
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  Apple Weather
                </a>
                .
              </p>
            </li>
          )}
        </ul>
      </section>
    </>
  )
}
